import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * IBAN format validator. Checks:
 * 1. 2 uppercase letters (country code) + 2 digits (check digits) + 1-30 alphanumeric (BBAN)
 * 2. Total length 5-34 characters
 * 3. MOD-97 checksum validation (ISO 13616)
 */
export function ibanValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null; // let Validators.required handle empty

    const clean = value.replace(/\s/g, '').toUpperCase();

    // Basic format: 2 letters + 2 digits + 1-30 alphanumeric
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(clean)) {
      return { iban: 'Invalid IBAN format' };
    }

    // MOD-97 check (ISO 13616)
    // Move first 4 chars to end, convert letters to numbers (A=10, B=11, ..., Z=35)
    const rearranged = clean.slice(4) + clean.slice(0, 4);
    const numeric = rearranged.replace(/[A-Z]/g, (ch: string) => (ch.charCodeAt(0) - 55).toString());

    // Calculate mod 97 using string chunking (avoids BigInt for browser compat)
    let remainder = '';
    for (const char of numeric) {
      remainder += char;
      const num = parseInt(remainder, 10);
      remainder = (num % 97).toString();
    }

    if (parseInt(remainder, 10) !== 1) {
      return { iban: 'Invalid IBAN checksum' };
    }

    return null;
  };
}

/**
 * BIC/SWIFT code validator.
 * Format: 4 letters (bank) + 2 letters (country) + 2 alphanumeric (location) + optional 3 alphanumeric (branch)
 * Total: 8 or 11 characters
 */
export function bicValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null; // let Validators.required handle empty

    const clean = value.replace(/\s/g, '').toUpperCase();

    if (!/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(clean)) {
      return { bic: 'Invalid BIC/SWIFT format' };
    }

    return null;
  };
}
